import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { SignInPage, SignUpPage, AuthCallback, ForgotPasswordPage, ResetPasswordPage, MfaChallengePage } from "@auth";
import { ErrorBoundary, AuthProvider, useAuth, EmailVerificationProvider, PersistentLayout, ConnectionError } from "@shared";
import { AccountPage } from "@accounts/AccountPage";
import { Dashboard } from "@shared/Dashboard";
import { SettingsPage } from "@settings/SettingsPage";
import { AssetsPage } from "./modules/assets/AssetsPage";
import { ProductsPage } from "./modules/products/ProductsPage";
import { CheckoutPage } from "./modules/checkout/CheckoutPage";
import { OrdersPage } from "./modules/orders/OrdersPage";
import { OrderDetailPage } from "./modules/orders/OrderDetailPage";
import { PaymentCallbackPage } from "./modules/payments/PaymentCallbackPage";
import { CatalogPage } from "./modules/catalog/CatalogPage";
import { CatalogProductDetailPage } from "./modules/catalog/CatalogProductDetailPage";


const ProtectedLayout = () => {
  const { user, loading, error, checkAuth } = useAuth();

  if (loading) return null;
  // Transient bootstrap failure (429/5xx/network): the refresh cookie may still
  // be valid, so offer a retry instead of bouncing a live session to sign-in.
  if (!user && error) return <ConnectionError message={error} onRetry={checkAuth} />;
  if (!user) return <Navigate to="/sign-in" replace />;

  return (
    <PersistentLayout>
      <Outlet />
    </PersistentLayout>
  );
};

// Create a client
const queryClient = new QueryClient();

export const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <EmailVerificationProvider>
            <Routes>
              {/* Auth routes - no navigation */}
              <Route path="/sign-in" element={<SignInPage />} />
              <Route path="/sign-in/mfa" element={<MfaChallengePage />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              
              {/* Protected routes - with persistent navigation */}
              <Route element={<ProtectedLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/settings/*" element={<SettingsPage />} />
                <Route path="/catalog" element={<CatalogPage />} />
                <Route path="/catalog/:slug" element={<CatalogProductDetailPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/assets" element={<AssetsPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/orders/:id" element={<OrderDetailPage />} />
                <Route path="/payments/callback" element={<PaymentCallbackPage />} />
              </Route>

              <Route path="/" element={<Navigate to="/sign-in" replace />} />
            </Routes>
            </EmailVerificationProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Named export used in main.tsx
